import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { League, LeaguePayload } from '../../../models/league';
import { LeagueService } from '../../../services/league.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';
import { UserAlertService } from '../../../services/user-alert.service';
import { UserAlert } from '../../../models/user-alert';

@Component({
  selector: 'app-league-form',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AdminPageHeaderComponent],
  templateUrl: './league-form.component.html',
  styleUrls: ['./league-form.component.scss'],
})
export class LeagueFormComponent implements OnInit {
  title = 'Nova Liga';
  subtitle = 'Configure as informações essenciais da liga e publique quando estiver pronta.';
  isEditMode = false;
  isSaving = false;
  leagueId: number | null = null;

  league: Partial<LeaguePayload> = {
    name: '',
    description: '',
    league_type: 'DAILY',
    league_access: 'ALL',
    entry_cost: 400,
    available_free_boosts: 0,
    available_paid_boosts: 0,
    rules: '',
    cashback: 0,
    can_players_join: true,
    owner_id: undefined,
  };

  startsAtLocal = '';
  coverFile: File | null = null;
  ownerSearch = '';
  ownerOptions: UserAlert[] = [];
  selectedOwnerLabel = '';
  feedbackError = '';
  feedbackSuccess = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leagueService: LeagueService,
    private userAlertService: UserAlertService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!idParam;

    if (this.isEditMode && idParam) {
      this.leagueId = Number(idParam);
      this.title = 'Editar Liga';
      this.subtitle = 'Atualize os dados da liga mantendo as regras operacionais.';
      this.loadLeague(this.leagueId);
    } else {
      this.applyDefaultsByType();
    }
  }

  loadLeague(id: number): void {
    this.leagueService.get(id).subscribe({
      next: (league: League) => {
        this.league = {
          name: league.name,
          description: league.description,
          league_type: league.league_type,
          league_access: league.league_access,
          minimum_players: league.minimum_players,
          entry_cost: league.entry_cost,
          rounds: league.rounds,
          round_duration: league.round_duration,
          available_free_boosts: league.available_free_boosts,
          available_paid_boosts: league.available_paid_boosts,
          owner_id: league.owner?.id || league.owner_id,
          rules: league.rules || '',
          cashback: league.cashback || 0,
          can_players_join: league.can_players_join ?? true,
        };

        this.startsAtLocal = this.toLocalDateInput(league.starts_at);
        this.selectedOwnerLabel = league.owner?.username || `ID ${league.owner_id}`;
        this.feedbackError = '';
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível carregar a liga.');
      },
    });
  }

  toLocalDateInput(isoDate: string): string {
    const date = new Date(isoDate);
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  onTypeChange(): void {
    this.applyDefaultsByType();
  }

  onAccessChange(): void {
    this.applyMinimumPlayersByAccess();
  }

  applyDefaultsByType(): void {
    if (this.league.league_type === 'SERIES') {
      this.league.rounds = 2;
      this.league.round_duration = 5;
    } else {
      this.league.rounds = 1;
      if (!this.league.round_duration || this.league.round_duration < 2 || this.league.round_duration > 4) {
        this.league.round_duration = 2;
      }
    }
    this.applyMinimumPlayersByAccess();
  }

  applyMinimumPlayersByAccess(): void {
    this.league.minimum_players = this.league.league_access === 'ALL' ? 16 : 2;
  }

  onCoverSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.coverFile = file;
  }

  searchOwners(): void {
    this.userAlertService.listUsers(this.ownerSearch, { page: 1 }).subscribe({
      next: (response) => {
        this.ownerOptions = response.results || [];
        this.feedbackError = '';
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível buscar owners.');
      },
    });
  }

  selectOwner(owner: UserAlert): void {
    this.league.owner_id = owner.id;
    this.selectedOwnerLabel = `${owner.username} (#${owner.id})`;
    this.feedbackError = '';
  }

  extractErrorMessage(error: any, fallback: string): string {
    const response = error?.error;
    if (!response) {
      return fallback;
    }

    if (typeof response === 'string') {
      return response;
    }

    if (typeof response?.detail === 'string') {
      return response.detail;
    }

    const fieldMessages: string[] = [];
    Object.entries(response).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        fieldMessages.push(`${field}: ${value.join(', ')}`);
      } else if (typeof value === 'string') {
        fieldMessages.push(`${field}: ${value}`);
      }
    });

    if (fieldMessages.length > 0) {
      return fieldMessages.join(' | ');
    }

    return fallback;
  }

  async getCoverFileOrDefault(): Promise<File> {
    if (this.coverFile) {
      return this.coverFile;
    }

    const response = await fetch('/images/league-default-cover.svg');
    const blob = await response.blob();
    return new File([blob], 'league-default-cover.svg', {
      type: 'image/svg+xml',
    });
  }

  async submit(): Promise<void> {
    if (!this.league.owner_id || !this.startsAtLocal || !this.league.name || !this.league.description) {
      this.feedbackError = 'Preencha os campos obrigatórios (nome, descrição, owner e início).';
      this.feedbackSuccess = '';
      return;
    }

    this.feedbackError = '';
    this.feedbackSuccess = '';
    this.isSaving = true;
    try {
      const formData = new FormData();
      const startsAtIso = new Date(this.startsAtLocal).toISOString();
      const coverImage = await this.getCoverFileOrDefault();

      formData.append('name', String(this.league.name));
      formData.append('description', String(this.league.description));
      formData.append('starts_at', startsAtIso);
      formData.append('game', 'LEAGUE_OF_LEGENDS');
      formData.append('league_type', String(this.league.league_type));
      formData.append('league_team', 'SOLO');
      formData.append('league_access', String(this.league.league_access));
      formData.append('minimum_players', String(this.league.minimum_players));
      formData.append('entry_cost', String(this.league.entry_cost));
      formData.append('rounds', String(this.league.rounds));
      formData.append('round_duration', String(this.league.round_duration));
      formData.append('available_free_boosts', String(this.league.available_free_boosts ?? 0));
      formData.append('available_paid_boosts', String(this.league.available_paid_boosts ?? 0));
      formData.append(
        'can_buy_boost',
        String((this.league.available_paid_boosts ?? 0) > 0)
      );
      formData.append('owner_id', String(this.league.owner_id));
      formData.append('rules', String(this.league.rules ?? ''));
      formData.append('cashback', String(this.league.cashback ?? 0));
      formData.append('can_players_join', String(this.league.can_players_join ?? true));
      formData.append('cover_image', coverImage);

      if (this.isEditMode && this.leagueId) {
        this.leagueService.update(this.leagueId, formData).subscribe({
          next: () => {
            this.isSaving = false;
            this.feedbackSuccess = 'Liga atualizada com sucesso.';
            this.router.navigate(['/dashboard/ligas']);
          },
          error: (error) => {
            this.isSaving = false;
            this.feedbackError = this.extractErrorMessage(error, 'Não foi possível atualizar a liga.');
          },
        });
      } else {
        this.leagueService.create(formData).subscribe({
          next: () => {
            this.isSaving = false;
            this.feedbackSuccess = 'Liga criada com sucesso.';
            this.router.navigate(['/dashboard/ligas']);
          },
          error: (error) => {
            this.isSaving = false;
            this.feedbackError = this.extractErrorMessage(error, 'Não foi possível criar a liga.');
          },
        });
      }
    } catch {
      this.isSaving = false;
      this.feedbackError = 'Erro inesperado ao montar o envio da liga.';
    }
  }
}
